import { Basket, Device } from "@/services/model";
import { mobileAbos, ServiceIDs } from "@/utils/constants";
import {
  combineProductsByBaselabel,
  Duration,
  getFlatHomeBaseDisplayValue,
  isFlatHomeTariff,
  isFlatMobileTariff,
  TransformedProduct,
  TransformedProductsById,
} from "@/utils/tariffUtils";

export const isSmartphoneInBasket = (basket?: Basket): boolean =>
  isDeviceSmartphone(basket?.entries[0]?.device);

export const isDeviceSmartphone = (device?: Device): boolean =>
  !!device && device.id === 1;

export const getMobileAbos = (
  processedProductsById: TransformedProductsById | undefined,
) =>
  processedProductsById
    ? Object.values(processedProductsById || {}).filter((product) =>
        mobileAbos.includes(`${product.id}`),
      )
    : [];

export const getSortedMobileAbosMonthly = (
  mobileAbos: TransformedProduct[],
) => {
  // Define the desired order by productId
  const desiredOrder = [
    ServiceIDs.FlatMobileSwissMonth,
    ServiceIDs.FlatMobileMonth,
    ServiceIDs.FlatMobilePlusMonth,
  ];

  return mobileAbos
    .filter((abo) => abo.duration === Duration.month)
    .sort((a, b) => desiredOrder.indexOf(a.id!) - desiredOrder.indexOf(b.id!));
};

export const getSortedMobileAbosYearly = (mobileAbos: TransformedProduct[]) => {
  // Define the desired order by productId
  const desiredOrder = [
    ServiceIDs.FlatMobileSwissYear,
    ServiceIDs.FlatMobileYear,
    ServiceIDs.FlatMobilePlusYear,
  ];

  return mobileAbos
    .filter((abo) => abo.duration === Duration.year)
    .sort((a, b) => desiredOrder.indexOf(a.id!) - desiredOrder.indexOf(b.id!));
};

/** Data-only tariffs (excludes Flat Mobile voice abos and Flat Home). */
export const getDataOnlyProducts = (
  processedProductsById: TransformedProductsById | undefined,
) =>
  Object.entries(combineProductsByBaselabel(processedProductsById ?? {}))
    .filter(
      ([rate, [monthOption]]) =>
        !isFlatMobileTariff(rate) && !isFlatHomeTariff(monthOption?.id),
    )
    .map(([, [monthOption]]) => monthOption)
    .filter(Boolean);

const flatHomeProductOrder = [ServiceIDs.FlatHome4G, ServiceIDs.FlatHome5G];

/** Flat Home 4G/5G monthly options, sorted for display. */
export const getFlatHomeProducts = (
  processedProductsById: TransformedProductsById | undefined,
) =>
  Object.entries(combineProductsByBaselabel(processedProductsById ?? {}))
    .map(([, [monthOption]]) => monthOption)
    .filter(
      (product): product is TransformedProduct =>
        !!product && isFlatHomeTariff(product.id),
    )
    .sort(
      (a, b) =>
        flatHomeProductOrder.indexOf(a.id!) -
        flatHomeProductOrder.indexOf(b.id!),
    );

const flatHomeFallbackProduct = (
  id: (typeof flatHomeProductOrder)[number],
): TransformedProduct => ({
  id,
  baseDisplayValue: getFlatHomeBaseDisplayValue(id),
  chargeFlat: "",
  isVoiceProduct: false,
  data: id,
  voice: id,
});

/**
 * Flat Home options for partner flows. The smartphone options endpoint (131001)
 * often omits Flat Home; fall back to catalogue ids so the tariff stays selectable.
 */
export const getFlatHomeProductsWithFallback = (
  processedProductsById: TransformedProductsById | undefined,
) => {
  const fromApi = getFlatHomeProducts(processedProductsById);

  if (fromApi.length > 0) {
    return fromApi;
  }

  return flatHomeProductOrder.map(flatHomeFallbackProduct);
};
